import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";
import {
  useEditDoctorsMasterData,
  useDoctorMasterDataWithoutPagination,
} from "../../hooks/useDoctorMasterData";
import { toast } from "react-toastify";

const EditHCP = ({ open, onClose, hcpData, onInputChange }) => {
  const { mutate: editHCP } = useEditDoctorsMasterData(hcpData.id);
  const { data: existingDoctors } = useDoctorMasterDataWithoutPagination();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const checkDuplicates = () => {
    if (!existingDoctors || !Array.isArray(existingDoctors)) return false;
    const duplicates = existingDoctors.filter((hcp) => {
      const hcpEmail = hcp?.email ? hcp.email.toLowerCase() : "";
      const currentEmail = hcpData?.email ? hcpData.email.toLowerCase() : "";

      return (
        hcp.id !== hcpData.id && // Exclude current HCP from duplicate check
        (hcp.national_id === hcpData.national_id ||
          hcp.APO_number === hcpData.APO_number ||
          hcpEmail === currentEmail)
      );
    });

    if (duplicates.length > 0) {
      const duplicateFields = [];
      duplicates.forEach((hcp) => {
        if (hcp.national_id === hcpData.national_id)
          duplicateFields.push("National ID");
        if (hcp.APO_number === hcpData.APO_number)
          duplicateFields.push("APO Number");
        if (hcp.email && hcp.email.toLowerCase() === hcpData.email.toLowerCase())
          duplicateFields.push("Email");
      });

      toast.error(`Duplicate found for: ${duplicateFields.join(", ")}`);

      return true;
    }
    return false;
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Check for duplicates
      if (checkDuplicates()) {
        setIsSubmitting(false);
        return;
      }

      const rawUpdateData = {
        first_name: hcpData.first_name,
        family_name: hcpData.family_name,
        email: hcpData.email,
        phone_number: hcpData.phone_number,
        specialty: hcpData.specialty,
        organization: hcpData.organization,
        medical_license: hcpData.medical_license,
        national_id: hcpData.national_id,
        APO_number: hcpData.APO_number,
        industry_area: hcpData.industry_area,
        city_id: hcpData.city_id,
        country_id: hcpData.country_id,
        updated_at: new Date().toISOString(), // Add updated_at here
      };

      // Filter out undefined values
      const updateData = Object.fromEntries(
        Object.entries(rawUpdateData).filter(
          ([_, value]) => value !== undefined
        )
      );

      // Wait for the mutation to complete
      await editHCP({
        id: hcpData.id,
        data: updateData,
      });

      toast.success("HCP updated successfully");
      onClose();
    } catch (error) {
      console.error("Error updating HCP:", error);
      toast.error(error.message || "Error updating HCP");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className="bg-blueColor text-white py-4">
        <i className="fas fa-edit text-xl mx-2"></i>
        <span className="text-xl font-semibold">Edit HCP</span>
      </DialogTitle>
      <DialogContent className="mt-4">
        <div className="grid grid-cols-2 gap-4 p-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              name="first_name"
              value={hcpData.first_name}
              onChange={onInputChange}
              type="text"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Family Name <span className="text-red-500">*</span>
            </label>
            <input
              name="family_name"
              value={hcpData.family_name}
              onChange={onInputChange}
              type="text"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              name="email"
              value={hcpData.email}
              onChange={onInputChange}
              type="email"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Phone Number
            </label>
            <input
              name="phone_number"
              value={hcpData.phone_number}
              onChange={onInputChange}
              type="tel"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Specialty
            </label>
            <input
              name="specialty"
              value={hcpData.specialty}
              onChange={onInputChange}
              type="text"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Organization
            </label>
            <input
              name="organization"
              value={hcpData.organization}
              onChange={onInputChange}
              type="text"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Medical License <span className="text-red-500">*</span>
            </label>
            <input
              name="medical_license"
              value={hcpData.medical_license}
              onChange={onInputChange}
              type="text"
              required
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              National ID <span className="text-red-500">*</span>
            </label>
            <input
              name="national_id"
              value={hcpData.national_id}
              onChange={onInputChange}
              type="text"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              APO Number <span className="text-red-500">*</span>
            </label>
            <input
              name="APO_number"
              value={hcpData.APO_number}
              onChange={onInputChange}
              type="text"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Practice/Sector
            </label>
            <select
              name="industry_area"
              onChange={onInputChange}
              value={hcpData.industry_area}
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
            >
              <option value="" hidden>
                Select Practice/Sector
              </option>
              <option value="private">Private</option>
              <option value="public">Public</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-4 mt-6 p-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            data-track="edit-hcp-button"
            data-module="HCPs"
            onClick={handleSubmit}
            className="px-4 py-2 bg-blueColor text-white rounded"
            disabled={isSubmitting}
          >
            Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditHCP;
